import toast from "react-hot-toast";
import classNames from "classnames";
import axiosInstance from "helpers/api";
import Button from "components/base/Button";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SearchBox from "components/common/SearchBox";
import { Form, Modal, Badge } from "react-bootstrap";
import NoDataFound from "components/common/NoDataFound";
import { SubmitHandler, useForm } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Attachment, LeadDetailsAttachmentsProps } from "interfaces/lead";
import { faPlus, faTimes, faSearch } from "@fortawesome/free-solid-svg-icons";
import FileListItem from "components/modules/project-management/todo-list/FileListItem";
import "./LeadAttachmentsResponsive.css";

type Inputs = {
  attachment: FileList;
};

const LeadDetailsAttachments = ({ attachmentsLv }: LeadDetailsAttachmentsProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Inputs>();
  const [open, setOpen] = useState(false);
  const [attachmentList, setAttachmentList] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredAttachments, setFilteredAttachments] = useState<Attachment[]>([]);
  const { id } = useParams();

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    setLoading(true);
    const formData = new FormData();
    formData.append("leadAttachment", data.attachment[0]);

    try {
      const response = await axiosInstance.post(
        `/leads/${id}/attachments`,
        formData
      );
      setAttachmentList(response.data.data.attachments);
      toast.success(response.data.message);
      reset();
      setOpen(false);
      setLoading(false);
    } catch (error: any) {
      toast.error(error.response.data.message);
      setLoading(true);
    }
  };

  useEffect(() => {
    if (attachmentsLv && Array.isArray(attachmentsLv)) {
      setAttachmentList(attachmentsLv);
    } else {
      setAttachmentList([]);
    }
  }, [attachmentsLv]);

  const handleDeleteAttachment = async (attachmentId: string) => {
    try {
      const response = await axiosInstance.delete(
        `/leads/${id}/attachments/${attachmentId}`
      );
      toast.success(response.data.message);
      setAttachmentList(
        attachmentList.filter(
          (attachment: Attachment) => attachment.id !== attachmentId
        )
      );
    } catch (error: any) {
      toast.error(error.response.data.message);
    }
  };

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredAttachments(attachmentList);
    } else {
      const filtered = attachmentList.filter((attachment: Attachment) => {
        return attachment && attachment.attachment_title &&
          attachment.attachment_title.toLowerCase().includes(searchQuery.toLowerCase());
      });
      setFilteredAttachments(filtered);
    }
  }, [attachmentList, searchQuery]);

  return (
    <>
      <div className="attachments-header mb-4">
        <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between mb-3">
          <div className="mb-2 mb-sm-0">
            <h2 className="mb-1 attachments-title" style={{ fontSize: '1.375rem' }}>Attachments</h2>
            <small className="text-muted d-none d-sm-block">Manage your lead attachments efficiently</small>
          </div>
          <Button
            variant="primary"
            onClick={() => setOpen(true)}
            className="attachment-create-btn"
            startIcon={<FontAwesomeIcon icon={faPlus} />}
          >
            Add Attachment
          </Button>
        </div>

        <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 gap-sm-3 mb-3">
          <SearchBox
            placeholder="Search attachments..."
            className="flex-grow-1"
            style={{ width: '100%', maxWidth: "30rem" }}
            onChange={(e: any) => setSearchQuery(e.target.value)}
            prepend={<FontAwesomeIcon icon={faSearch} className="text-muted" />}
          />
          <div className="d-flex align-items-center gap-2">
            <Badge bg="primary" className="attachments-count-badge" style={{ width: '110px', height: '24px', justifyContent: 'center', borderRadius: '5px' }}>
              {filteredAttachments.length} {filteredAttachments.length > 1 ? "attachments" : "attachment"}
            </Badge>
          </div>
        </div>
        
        <hr className="my-2" />
      </div>

      <div className="attachments-list">
        {filteredAttachments.length > 0 ? (
          filteredAttachments.map((attachment: Attachment, index: number) => (
            <FileListItem
              deleteAttachment={handleDeleteAttachment}
              key={index}
              attachment={attachment}
              className={classNames("border-dashed border-translucent", {
                "border-top": index === 0,
                "border-bottom-0": index === filteredAttachments.length - 1,
              })}
            />
          ))
        ) : (
          <NoDataFound type="Attachments" customClass="mt-lg-10" />
        )}
      </div>
      <Modal
        show={open}
        onHide={() => setOpen(false)}
        className="p-0 attachment-modal"
        centered
        contentClassName="border"
      >
        <Modal.Header className="border-translucent p-4">
          <div>
            <h5 className="modal-title text-body-highlight fs-6 lh-sm">
              Add Attachment
            </h5>
            <p className="mb-0 fs-9">.png, .jpg, .pdf and .txt files are allowed.</p>
          </div>

          <Button className="p-1 ms-auto" onClick={() => setOpen(false)}>
            <FontAwesomeIcon icon={faTimes} className="fs-7 text-danger" />
          </Button>
        </Modal.Header>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body className="pt-4 pb-2 px-4">
            <div className="mb-3">
              <Form.Group>
                <Form.Label className="form-label-header mb-2">
                  Attachment
                </Form.Label>
                <Form.Control
                  accept=".png, .jpg, .pdf, .txt"
                  type="file"
                  placeholder="Enter Topic"
                  {...register("attachment", {
                    required: "File is required",
                    validate: {
                      lessThan10MB: (files) =>
                        files[0]?.size < 10 * 1024 * 1024 ||
                        "File size should be less than 10MB",
                    },
                  })}
                />
              </Form.Group>
              {errors.attachment && (
                <p style={{ color: "red" }}>
                  {String(errors.attachment.message)}
                </p>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-end align-items-center px-4 pb-4 border-0 pt-3">
            <Button
              variant="phoenix-primary"
              size="sm"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              className="fs-9 my-0"
              disabled={loading}
            >
              {loading ? "Uploading..." : "Add"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default LeadDetailsAttachments;
